import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {Clear as ClearIcon, Delete as DeleteIcon, Edit as EditIcon, FilterAlt as FilterAltIcon, FilterAltOff,} from '@mui/icons-material';
import 'dayjs/locale/de';
import dayjs from 'dayjs';
import {GenehmigteAntraegeTabProps, UrlaubsantragWithID} from '../../../../Contexts/Urlaubsantrag/UrlaubsantragContext';
import {UrlaubsantragEditDialog} from './UrlaubsantragEditDialog';

export const GenehmigteAntraegeTab: React.FC<GenehmigteAntraegeTabProps> = ({
                                                                                loading,
                                                                                antraege,
                                                                                onDelete,
                                                                                onEdit,
                                                                            }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Filter states
    const [editAntrag, setEditAntrag] = useState<UrlaubsantragWithID | null>(null);
    const [mitarbeiterFilter, setMitarbeiterFilter] = useState<string>('');
    const [mitarbeiterOptions, setMitarbeiterOptions] = useState<string[]>([]);
    const [vonDatum, setVonDatum] = useState<dayjs.Dayjs | null>(null);
    const [bisDatum, setBisDatum] = useState<dayjs.Dayjs | null>(null);
    const [filteredAntraege, setFilteredAntraege] = useState(antraege);
    const [showFilters, setShowFilters] = useState(false);

    // Format date for display
    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('DD.MM.YYYY');
    };

    // Extract unique employee names for filter dropdown
    useEffect(() => {
        const uniqueNames = Array.from(new Set(antraege.map((antrag) => antrag.username)));
        setMitarbeiterOptions(uniqueNames);
    }, [antraege]);

    // Apply filters whenever filter values or antraege change
    useEffect(() => {
        let result = [...antraege];

        // Filter by employee name if selected
        if (mitarbeiterFilter) {
            result = result.filter((antrag) => antrag.username === mitarbeiterFilter);
        }

        // Filter by date range if selected
        if (vonDatum) {
            result = result.filter(
                (antrag) =>
                    dayjs(antrag.ende).isAfter(vonDatum, 'day') || dayjs(antrag.ende).isSame(vonDatum, 'day')
            );
        }

        if (bisDatum) {
            result = result.filter(
                (antrag) =>
                    dayjs(antrag.beginn).isBefore(bisDatum, 'day') ||
                    dayjs(antrag.beginn).isSame(bisDatum, 'day')
            );
        }

        setFilteredAntraege(result);
    }, [antraege, mitarbeiterFilter, vonDatum, bisDatum]);

    // Reset all filters
    const handleResetFilters = () => {
        setMitarbeiterFilter('');
        setVonDatum(null);
        setBisDatum(null);
    };

    // Filter component
    const FilterComponent = () => (
        <Box sx={{mb: 2, py: 2}}>
            <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} md={3}>
                    <FormControl fullWidth size='small'>
                        <InputLabel id='mitarbeiter-filter-label'>Mitarbeiter</InputLabel>
                        <Select
                            labelId='mitarbeiter-filter-label'
                            id='mitarbeiter-filter'
                            value={mitarbeiterFilter}
                            label='Mitarbeiter'
                            onChange={(e) => setMitarbeiterFilter(e.target.value as string)}>
                            <MenuItem value=''>Alle</MenuItem>
                            {mitarbeiterOptions.map((name) => (
                                <MenuItem key={name} value={name}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                    <Grid item xs={12} md={3}>
                        <DatePicker
                            label='Von Datum'
                            value={vonDatum}
                            onChange={(newValue) => setVonDatum(newValue)}
                            format='DD.MM.YYYY'
                            slotProps={{textField: {size: 'small', fullWidth: true}}}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <DatePicker
                            label='Bis Datum'
                            value={bisDatum}
                            onChange={(newValue) => setBisDatum(newValue)}
                            format='DD.MM.YYYY'
                            slotProps={{textField: {size: 'small', fullWidth: true}}}
                        />
                    </Grid>
                </LocalizationProvider>
                <Grid item xs={12} md={3}>
                    <Button variant='outlined' onClick={handleResetFilters} fullWidth>
                        <FilterAltOff/>
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (antraege.length === 0) {
        return (
            <Box sx={{p: 2}}>
                <Typography variant='body1'>Keine genehmigten Urlaubsanträge vorhanden</Typography>
            </Box>
        );
    }

    // Mobile layout with cards
    if (isMobile) {
        return (
            <Box sx={{p: 1}}>
                <Button
                    variant='outlined'
                    startIcon={showFilters ? <ClearIcon/> : <FilterAltIcon/>}
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{mb: 2}}
                    fullWidth>
                    {showFilters ? 'Filter ausblenden' : 'Filter anzeigen'}
                </Button>

                {showFilters && <FilterComponent/>}

                {filteredAntraege.length === 0 ? (
                    <Box sx={{p: 2, textAlign: 'center'}}>
                        <Typography variant='body1'>Keine Anträge entsprechen den Filterkriterien</Typography>
                    </Box>
                ) : (
                    filteredAntraege.map((antrag) => (
                        <Paper
                            key={antrag.id}
                            elevation={2}
                            sx={{
                                mb: 2,
                                overflow: 'hidden',
                                borderRadius: 1,
                            }}>
                            <Box
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                                }}>
                                <Box>
                                    <Typography variant='subtitle1' fontWeight='medium'>
                                        {antrag.username}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        {formatDate(antrag.beginn)} - {formatDate(antrag.ende)}
                                    </Typography>
                                </Box>
                                {antrag.status === 'Genehmigt' && (
                                    <Box>
                                        {onEdit && (
                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={() => setEditAntrag(antrag)}
                                                title='Antrag bearbeiten'>
                                                <EditIcon/>
                                            </IconButton>
                                        )}
                                        <IconButton
                                            size='small'
                                            color='error'
                                            onClick={() => onDelete(antrag.id)}
                                            title='Antrag löschen'>
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                            <Divider/>
                            <Box sx={{p: 2}}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant='body2' color='text.secondary'>
                                            Grund:
                                        </Typography>
                                        <Typography variant='body1'>{antrag.grund || '-'}</Typography>
                                    </Grid>
                                    {antrag?.notiz && (
                                        <Grid item xs={12}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Notizen:
                                            </Typography>
                                            <Typography variant='body1'>{antrag.notiz || '-'}</Typography>
                                        </Grid>
                                    )}
                                    <Grid item xs={12}>
                                        <Typography variant='body2' color='text.secondary'>
                                            Genehmigt am:
                                        </Typography>
                                        <Typography variant='body1'>
                                            {antrag.bearbeitung_datum ? formatDate(antrag.bearbeitung_datum) : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant='body2' color='text.secondary'>
                                            Genehmigt von:
                                        </Typography>
                                        <Typography variant='body1'>{antrag.bearbeiter_name || '-'}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    ))
                )}
                {onEdit && (
                    <UrlaubsantragEditDialog
                        open={!!editAntrag}
                        antrag={editAntrag}
                        loading={loading}
                        onClose={() => setEditAntrag(null)}
                        onSave={onEdit}
                    />
                )}
            </Box>
        );
    }

    // Desktop layout with table
    return (
        <Box>
            <FilterComponent/>

            {filteredAntraege.length === 0 ? (
                <Box sx={{p: 2, textAlign: 'center'}}>
                    <Typography variant='body1'>Keine Anträge entsprechen den Filterkriterien</Typography>
                </Box>
            ) : (
                <TableContainer sx={{overflowX: 'auto'}}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 'bold'}}>Mitarbeiter</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Von</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Bis</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Grund</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Notizen</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Urlaubstage</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Genehmigt am</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Genehmigt von</TableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAntraege.map((antrag) => (
                                <TableRow key={antrag.id}>
                                    <TableCell>{antrag.username}</TableCell>
                                    <TableCell>{formatDate(antrag.beginn)}</TableCell>
                                    <TableCell>{formatDate(antrag.ende)}</TableCell>
                                    <TableCell>{antrag.grund || '-'}</TableCell>
                                    <TableCell>{antrag?.notiz || '-'}</TableCell>
                                    <TableCell>{antrag?.working_days || '-'}</TableCell>
                                    <TableCell>
                                        {antrag.bearbeitung_datum ? formatDate(antrag.bearbeitung_datum) : '-'}
                                    </TableCell>
                                    <TableCell>{antrag.bearbeiter_name || '-'}</TableCell>
                                    <TableCell>
                                        {antrag.status === 'Genehmigt' && (
                                            <>
                                                {onEdit && (
                                                    <IconButton
                                                        size='small'
                                                        color='primary'
                                                        onClick={() => setEditAntrag(antrag)}
                                                        title='Antrag bearbeiten'>
                                                        <EditIcon/>
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    size='small'
                                                    color='error'
                                                    onClick={() => onDelete(antrag.id)}
                                                    title='Antrag löschen'>
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {onEdit && (
                <UrlaubsantragEditDialog
                    open={!!editAntrag}
                    antrag={editAntrag}
                    loading={loading}
                    onClose={() => setEditAntrag(null)}
                    onSave={onEdit}
                />
            )}
        </Box>
    );
};
